import { API_BASE_URL } from "../api";
import { encodeWav } from "../video/subs/activity";

export const CHAT_API = `${API_BASE_URL}/core/chatbot/v1`;
// The address clients are told to use: the live website's API (the page itself talks to whatever server it was built for, which in development is the local one).
export const PUBLIC_API_BASE = (process.env.REACT_APP_PUBLIC_API_URL || "https://mraalionline.com/api").replace(/\/+$/, "");

// ---- showing an answer: a small, safe formatter (the text is never put in the page as HTML, so nothing in an answer can run) ----
// "**bold**" and `code` inside a line -> [{ kind: "text" | "bold" | "code", text }]
export function inlineParts(line) {
  const parts = [];
  const pattern = /\*\*([^*\n]+)\*\*|`([^`\n]+)`/g;
  let last = 0;
  let m = pattern.exec(line);
  while (m) {
    if (m.index > last) parts.push({ kind: "text", text: line.slice(last, m.index) });
    parts.push(m[1] !== undefined ? { kind: "bold", text: m[1] } : { kind: "code", text: m[2] });
    last = m.index + m[0].length;
    m = pattern.exec(line);
  }
  if (last < line.length) parts.push({ kind: "text", text: line.slice(last) });
  return parts;
}

// The answer as blocks: paragraphs (lines kept), bullet lists and numbered lists.
export function parseBlocks(text) {
  const blocks = [];
  let current = null;
  const close = () => { if (current) blocks.push(current); current = null; };
  String(text || "").replace(/\r/g, "").split("\n").forEach((raw) => {
    const bullet = raw.match(/^\s*[-*•]\s+(.*)$/);
    const numbered = raw.match(/^\s*\d+[.)]\s+(.*)$/);
    if (bullet || numbered) {
      const type = bullet ? "ul" : "ol";
      if (!current || current.type !== type) { close(); current = { type, items: [] }; }
      current.items.push(inlineParts((bullet || numbered)[1]));
    } else if (!raw.trim()) {
      close();
    } else {
      if (!current || current.type !== "p") { close(); current = { type: "p", lines: [] }; }
      current.lines.push(inlineParts(raw.trim()));
    }
  });
  close();
  return blocks;
}

// ---- the answer arrives as server-sent events ("data: {...}" separated by blank lines) ----
export function parseEvents(buffer) {
  const pieces = buffer.split("\n\n");
  const rest = pieces.pop();
  const events = [];
  pieces.forEach((piece) => {
    if (!piece.startsWith("data:")) return;
    try { events.push(JSON.parse(piece.slice(5))); } catch { /* a broken piece is skipped */ }
  });
  return { events, rest };
}

export async function readEvents(response, onEvent) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const { events, rest } = parseEvents(buffer);
    buffer = rest;
    events.forEach(onEvent);
  }
}

// Asks the chatbot and streams the answer. `body` is a JSON object (text) or FormData (a recording). onEvent gets: { start }, { delta }, { done, reply }, { error: { message } }.
export async function askChatbot({ widgetKey, body, onEvent, signal }) {
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;
  if (isForm) body.append("stream", "true");
  let response;
  try {
    response = await fetch(`${CHAT_API}/chat/`, {
      method: "POST", signal, headers: { "X-Widget-Key": widgetKey, ...(isForm ? {} : { "Content-Type": "application/json" }) },
      body: isForm ? body : JSON.stringify({ ...body, stream: true }),
    });
  } catch (err) {
    if (err && err.name === "AbortError") throw err;
    onEvent({ error: { message: "Could not reach the server. Check your connection and try again." } });
    return;
  }
  if (!response.ok || !(response.headers.get("content-type") || "").includes("event-stream")) {
    let message = "Sorry, something went wrong. Please try again.";
    try { message = (await response.json()).error.message || message; } catch { /* keep the general message */ }
    onEvent({ error: { message } });
    return;
  }
  await readEvents(response, onEvent);
}

// The server's voice for a piece of text: returns an audio Blob (or null).
export async function fetchSpeech(widgetKey, text, language) {
  try {
    const response = await fetch(`${CHAT_API}/speak/`, {
      method: "POST", headers: { "X-Widget-Key": widgetKey, "Content-Type": "application/json" }, body: JSON.stringify({ text, language: language && language !== "auto" ? language : undefined }),
    });
    return response.ok ? await response.blob() : null;
  } catch {
    return null;
  }
}

// ---- asking by voice ----
// A recording (any format the browser can play) as a 16 kHz mono WAV, which the server reads.
export async function recordingToWav(blob) {
  const Context = window.AudioContext || window.webkitAudioContext;
  const context = new Context();
  try {
    const decoded = await context.decodeAudioData(await blob.arrayBuffer());
    const offline = new OfflineAudioContext(1, Math.max(1, Math.round(decoded.duration * 16000)), 16000);
    const source = offline.createBufferSource();
    source.buffer = decoded;
    source.connect(offline.destination);
    source.start();
    const rendered = await offline.startRendering();
    return new Blob([encodeWav(rendered.getChannelData(0), 16000)], { type: "audio/wav" });
  } finally {
    if (context.close) context.close();
  }
}

const SPEECH_TAGS = { en: "en-US", ur: "ur-PK", hi: "hi-IN", pa: "pa-IN", ar: "ar-SA", fa: "fa-IR", tr: "tr-TR", bn: "bn-BD", id: "id-ID", fr: "fr-FR", de: "de-DE", es: "es-ES", pt: "pt-PT", it: "it-IT", nl: "nl-NL", pl: "pl-PL", el: "el-GR", ja: "ja-JP", ko: "ko-KR", zh: "zh-CN", vi: "vi-VN", ru: "ru-RU" };
// The language tag the browser's recogniser wants for a language code ("auto" -> the browser's own language).
export const speechTag = (code, fallback = "en-US") => (code && code !== "auto" ? SPEECH_TAGS[code] || code : fallback);

// ---- what a client is shown: ready-to-copy examples with the real server address ----
export function codeSamples(base, key = "YOUR_API_KEY", widgetKey = "YOUR_WIDGET_KEY") {
  const api = `${base}/core/chatbot/v1`;
  return {
    curl: `# Ask a question (text)
curl -X POST ${api}/chat/ \\
  -H "Authorization: Bearer ${key}" \\
  -H "Content-Type: application/json" \\
  -d '{"message": "What are your opening hours?"}'

# Continue the same conversation (use the conversation_id of the first answer)
curl -X POST ${api}/chat/ \\
  -H "Authorization: Bearer ${key}" \\
  -H "Content-Type: application/json" \\
  -d '{"message": "And on Sunday?", "conversation_id": "cv_..."}'

# Ask by voice: send an audio file (WAV, MP3, FLAC or OGG), add "speak=true" to get the answer spoken (base64 MP3 in "audio")
curl -X POST ${api}/chat/ \\
  -H "Authorization: Bearer ${key}" \\
  -F audio=@question.wav -F language=en -F speak=true`,
    javascript: `// Server-side (Node 18+) or any backend. Keep the key secret: never put it in a web page.
const res = await fetch("${api}/chat/", {
  method: "POST",
  headers: { "Authorization": "Bearer ${key}", "Content-Type": "application/json" },
  body: JSON.stringify({ message: "What are your opening hours?", conversation_id: savedId }),
});
const data = await res.json();
if (!res.ok) throw new Error(data.error.message);
console.log(data.reply);          // the answer
savedId = data.conversation_id;   // send it with the next question to keep the conversation

// Word by word (server-sent events): add  stream: true  to the body and read  data: {"delta": "..."}  lines.`,
    python: `import requests

API = "${api}"
HEADERS = {"Authorization": "Bearer ${key}"}

reply = requests.post(f"{API}/chat/", headers=HEADERS, json={"message": "What are your opening hours?"}, timeout=120)
data = reply.json()
if not reply.ok:
    raise RuntimeError(data["error"]["message"])
print(data["reply"])

# the next question of the same conversation
requests.post(f"{API}/chat/", headers=HEADERS, json={"message": "And on Sunday?", "conversation_id": data["conversation_id"]}, timeout=120)

# a spoken question
with open("question.wav", "rb") as f:
    spoken = requests.post(f"{API}/chat/", headers=HEADERS, files={"audio": f}, data={"language": "en"}, timeout=180).json()
print(spoken["transcript"], "->", spoken["reply"])`,
    php: `<?php
$ch = curl_init("${api}/chat/");
curl_setopt_array($ch, [
    CURLOPT_POST => true,
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => ["Authorization: Bearer ${key}", "Content-Type: application/json"],
    CURLOPT_POSTFIELDS => json_encode(["message" => "What are your opening hours?"]),
    CURLOPT_TIMEOUT => 120,
]);
$data = json_decode(curl_exec($ch), true);
echo $data["reply"] ?? $data["error"]["message"];`,
    embed: `<!-- Put this line before </body> on any page of the website that was allowed for this chatbot -->
<script src="${base}/core/chatbot/widget.js" data-key="${widgetKey}" async></script>

<!-- Optional: data-title="Ask Sara"  data-color="#0f766e"  data-position="left"  data-open="true" -->`,
  };
}

export const ERROR_CODES = [
  ["401 missing_key / invalid_key", "No API key was sent, or it is wrong or was revoked."],
  ["403 origin_not_allowed", "A web page used a widget key, but its website is not in the chatbot's allowed websites."],
  ["404 conversation_not_found", "The conversation_id is unknown (or belongs to another chatbot)."],
  ["400 empty_message / message_too_long / audio_unreadable / no_speech", "The question was empty, over 4000 characters, or the recording could not be read or had no speech."],
  ["429 rate_limited / daily_limit / visitor_limit", "Too many messages per minute, the chatbot's daily limit is used up, or one visitor sent too many. See the Retry-After header."],
  ["503 ai_unavailable", "The language model could not answer right now. Try again."],
];
